import React, { useEffect, useRef, useState } from "react";
import { X, Volume2, VolumeX, Navigation2, MapPin, CornerUpLeft, CornerUpRight, ArrowUp, RotateCcw, Flag } from "lucide-react";
import RideMap from "./RideMap";
import { mapsApi } from "../../lib/api";

const R = 6371000;
const toRad = (d) => (d * Math.PI) / 180;
function meters(a, b) {
  if (!a || !b || !Number.isFinite(a.lat) || !Number.isFinite(b.lat)) return Infinity;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function bearing(a, b) {
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return (Math.atan2(y, x) * 180) / Math.PI;
}
const fmtDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.max(10, Math.round(m / 10) * 10)} m`);
const stripHtml = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function ManeuverIcon({ m, className }) {
  const k = (m || "").toUpperCase();
  if (k.includes("LEFT")) return <CornerUpLeft className={className} />;
  if (k.includes("RIGHT")) return <CornerUpRight className={className} />;
  if (k.includes("UTURN")) return <RotateCcw className={className} />;
  if (k.includes("DESTINATION")) return <Flag className={className} />;
  return <ArrowUp className={className} />;
}

// Full-screen in-app turn-by-turn navigation with spoken voice guidance. No need to leave the app.
export default function InAppNav({ destination, label = "destination", onExit }) {
  const [pos, setPos] = useState(null);
  const [nav, setNav] = useState(null);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [err, setErr] = useState("");
  const spokenRef = useRef("");
  const headingRef = useRef(0);
  const lastRef = useRef(null);
  const fetchedRef = useRef(false);
  const mutedRef = useRef(false);
  mutedRef.current = muted;

  const speak = (t) => {
    if (mutedRef.current || !t || spokenRef.current === t || !window.speechSynthesis) return;
    spokenRef.current = t;
    try {
      const u = new SpeechSynthesisUtterance(t);
      u.rate = 1; u.lang = "en-IN";
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { /* speech not supported */ }
  };

  useEffect(() => {
    if (!navigator.geolocation) { setErr("Location isn't available on this device."); return undefined; }
    const id = navigator.geolocation.watchPosition(
      (p) => {
        const np = { lat: p.coords.latitude, lng: p.coords.longitude };
        if (lastRef.current && meters(lastRef.current, np) > 4) headingRef.current = bearing(lastRef.current, np);
        lastRef.current = np;
        setPos(np);
      },
      () => setErr("Turn on location permission to navigate."),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
    );
    return () => { navigator.geolocation.clearWatch(id); try { window.speechSynthesis.cancel(); } catch { /* noop */ } };
  }, []);

  useEffect(() => {
    if (!pos || !destination || fetchedRef.current) return;
    fetchedRef.current = true;
    mapsApi.navigate(`${pos.lat},${pos.lng}`, `${destination.lat},${destination.lng}`)
      .then((d) => { if (d && Array.isArray(d.steps) && d.steps.length) { setNav(d); setIdx(0); spokenRef.current = ""; } else setErr("No route found."); })
      .catch(() => setErr("Couldn't load the route."));
  }, [pos, destination]);

  const steps = nav?.steps || [];
  const cur = steps[idx];
  const next = steps[idx + 1];
  const distToManeuver = cur && pos ? meters(pos, cur.end) : null;
  const arrived = pos && destination && meters(pos, destination) < 40;

  useEffect(() => {
    if (!cur || !pos || arrived) return;
    const d = meters(pos, cur.end);
    if (d < 25 && idx < steps.length - 1) { setIdx(idx + 1); spokenRef.current = ""; return; }
    const plain = stripHtml(cur.instruction);
    if (d <= 180) speak(plain);
    else if (spokenRef.current === "") speak(`${plain} in ${fmtDist(d)}.`);
  }, [pos, idx, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (arrived) speak(`You have arrived at your ${label}.`); }, [arrived]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-graphite" data-testid="in-app-nav">
      <div className="relative flex-1">
        <RideMap
          drop={destination}
          activeDriver={pos ? { ...pos, heading: headingRef.current } : undefined}
          route={nav?.points || null}
          follow
          className="h-full w-full !rounded-none !border-0"
        />
        <button onClick={onExit} className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-ink shadow-lg" data-testid="nav-exit"><X className="h-5 w-5" /></button>
        <button onClick={() => setMuted((m) => !m)} className="absolute right-3 top-16 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-ink shadow-lg" data-testid="nav-mute">{muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5 text-brand" />}</button>
      </div>

      <div className="space-y-2 rounded-t-3xl bg-white p-4 shadow-[0_-8px_30px_rgba(0,0,0,0.18)]">
        {err ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-red-600" data-testid="nav-error"><MapPin className="h-4 w-4" /> {err}</div>
        ) : arrived ? (
          <div className="flex items-center gap-3" data-testid="nav-arrived"><Flag className="h-7 w-7 text-emerald-600" /><div><p className="text-lg font-extrabold text-ink">You've arrived</p><p className="text-xs text-ink-soft">at your {label}</p></div></div>
        ) : cur ? (
          <>
            <div className="flex items-center gap-3" data-testid="nav-maneuver">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand text-white"><ManeuverIcon m={cur.maneuver} className="h-6 w-6" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-xl font-extrabold text-ink" data-testid="nav-distance">{distToManeuver != null ? fmtDist(distToManeuver) : ""}</p>
                <p className="truncate text-sm font-semibold text-ink-soft" data-testid="nav-instruction">{stripHtml(cur.instruction)}</p>
              </div>
            </div>
            {next ? <p className="truncate pl-[60px] text-xs text-ink-muted" data-testid="nav-next">Then: {stripHtml(next.instruction)}</p> : null}
            <div className="flex items-center justify-between border-t border-surface-200 pt-2 text-xs text-ink-soft">
              <span>{nav?.distance_km != null ? `${nav.distance_km} km left` : ""}{nav?.duration_min != null ? ` · ~${nav.duration_min} min` : ""}</span>
              <span className="inline-flex items-center gap-1 font-semibold text-brand"><Navigation2 className="h-3.5 w-3.5" /> In-app navigation</span>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-ink-soft" data-testid="nav-loading"><Navigation2 className="h-4 w-4 animate-pulse text-brand" /> Finding the best route…</div>
        )}
      </div>
    </div>
  );
}
